import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { RefreshCcw, X, Sparkles } from "lucide-react";
import {
    onServiceWorkerUpdate,
    applyServiceWorkerUpdate,
} from "@/lib/registerSW";

/**
 * PwaUpdateBanner
 * ────────────────
 * Slides in from the bottom-center when the Service Worker has installed a
 * new version and is waiting to take control. Two actions:
 *   • "Odśwież" → tells the new SW to skipWaiting; controllerchange listener
 *     in registerSW auto-reloads the page (so the user sees the new bundle).
 *   • "Później" → hides the banner for this session; will re-appear on next
 *     load if the update is still pending.
 * A dismiss X button acts the same as "Później".
 */
export default function PwaUpdateBanner() {
    const [available, setAvailable] = useState(false);
    const [reloading, setReloading] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        // Reset the dismissal on each fresh page load - hooked via
        // `sessionStorage` (per-tab) so the banner stops nagging within one
        // browsing session but reappears next time.
        try {
            if (sessionStorage.getItem("pr_pwa_update_dismissed") === "1") {
                setDismissed(true);
            }
        } catch {
            /* ignore */
        }
        const off = onServiceWorkerUpdate(() => setAvailable(true));
        return off;
    }, []);

    const refresh = () => {
        setReloading(true);
        applyServiceWorkerUpdate();
        // Safety net: if `controllerchange` doesn't fire within 3 s, force
        // reload anyway (some browsers with a stale SW never fire it).
        setTimeout(() => window.location.reload(), 3000);
    };

    const later = () => {
        try {
            sessionStorage.setItem("pr_pwa_update_dismissed", "1");
        } catch {
            /* ignore */
        }
        setDismissed(true);
    };

    const visible = available && !dismissed;

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    key="pwa-update-banner"
                    data-testid="pwa-update-banner"
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 30 }}
                    transition={{ type: "spring", stiffness: 220, damping: 22 }}
                    className="fixed z-[70] bottom-5 left-1/2 -translate-x-1/2 w-[calc(100vw-2.5rem)] max-w-md rounded-lg border border-[color:var(--border-strong)] bg-[color:var(--bg-surface)] shadow-2xl overflow-hidden"
                    style={{
                        boxShadow:
                            "0 24px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(250,204,21,0.18)",
                    }}
                >
                    <div className="absolute -top-14 -right-14 w-40 h-40 rounded-full blur-3xl pointer-events-none bg-[color:var(--primary)]/25" />
                    <button
                        onClick={later}
                        aria-label="Zamknij"
                        data-testid="pwa-update-dismiss"
                        className="absolute top-2 right-2 w-7 h-7 rounded-md flex items-center justify-center text-[color:var(--text-secondary)] hover:text-white hover:bg-white/10 transition"
                    >
                        <X size={13} />
                    </button>
                    <div className="relative p-5 flex items-start gap-3">
                        <div className="w-10 h-10 rounded-md flex items-center justify-center bg-[color:var(--primary)]/15 text-[color:var(--primary)] shrink-0">
                            <Sparkles size={18} strokeWidth={1.8} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-display uppercase tracking-wider text-white text-sm mb-1">
                                Nowa wersja strony
                            </div>
                            <div className="text-xs text-[color:var(--text-secondary)] leading-relaxed">
                                Wgraliśmy świeże zmiany. Odśwież, żeby zobaczyć
                                najnowszą wersję.
                            </div>
                            <div className="mt-4 flex gap-2">
                                <button
                                    onClick={refresh}
                                    disabled={reloading}
                                    data-testid="pwa-update-refresh"
                                    className="btn-primary text-xs disabled:opacity-60"
                                >
                                    <RefreshCcw
                                        size={12}
                                        className={reloading ? "animate-spin" : ""}
                                    />
                                    {reloading ? "Odświeżam…" : "Odśwież"}
                                </button>
                                <button
                                    onClick={later}
                                    className="btn-ghost text-xs"
                                    data-testid="pwa-update-later"
                                >
                                    Później
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
